// Page de connexion unifiée AbracadaWorld
import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff, LogIn, AlertCircle, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConnexionAcheteurPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Récupérer le paramètre redirect si présent
  const searchParams = new URLSearchParams(location.search);
  const redirectPath = searchParams.get('redirect');

  // Vérifier si déjà connecté au chargement
  useEffect(() => {
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token');
    const userType = localStorage.getItem('user_type');
    
    if (token) {
      // Déjà connecté, rediriger vers le bon cockpit
      if (redirectPath) {
        navigate(redirectPath);
      } else if (userType === 'professionnel') {
        navigate('/cockpit-pro');
      } else {
        navigate('/cockpit-particulier');
      }
    }
  }, [navigate, redirectPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Appel API de connexion
      const response = await fetch(`${API_URL}/api/ecosystem/auth/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Identifiants incorrects');
      }

      const data = await response.json();
      
      // Construire l'objet utilisateur complet pour la persistance
      const userData = {
        id: data.user?.id || data.profile?.user_id,
        name: data.user?.name || data.profile?.display_name || '',
        first_name: data.user?.first_name || data.profile?.display_name?.split(' ')[0] || '',
        last_name: data.user?.last_name || '',
        email: data.user?.email || email,
        trust_level: data.user?.trust_level || data.profile?.trust_level || null
      };
      
      // Stocker le token et les infos utilisateur pour la persistance
      localStorage.setItem('ecosystem_token', data.token);
      localStorage.setItem('ecosystem_user', JSON.stringify(userData));
      localStorage.setItem('buyer_token', data.token);
      localStorage.setItem('buyer_user', JSON.stringify(userData));
      localStorage.setItem('user_id', userData.id);
      localStorage.setItem('user_name', userData.name);
      localStorage.setItem('user_email', userData.email);
      
      // Déterminer le type d'utilisateur
      const userType = data.profile?.user_type || data.user?.user_type || 'particulier';
      localStorage.setItem('user_type', userType);
      
      toast.success(`Bienvenue ${userData.first_name || userData.name} !`);
      
      // Rediriger vers le bon cockpit selon le type
      if (redirectPath) {
        navigate(redirectPath);
      } else if (userType === 'professionnel') {
        navigate('/cockpit-pro');
      } else {
        navigate('/cockpit-particulier');
      }
      
    } catch (err) {
      setError(err.message || 'Identifiants incorrects');
      toast.error(err.message || 'Identifiants incorrects');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center pt-4 px-4 pb-24 relative">
      {/* Bouton X - Fermer */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-3 right-3 z-50 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 shadow-sm hover:bg-gray-100 transition-colors"
        data-testid="acheteur-login-close-btn"
      >
        <X size={18} className="text-gray-600" />
      </button>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >

        {/* Logo et titre - Fond blanc */}
        <div className="text-center mb-4">
          <Link to="/abracadaworld" className="inline-block mb-2">
            <img 
              src="/logo-abracadaworld-transparent.png" 
              alt="AbracadaWorld"
              className="h-12 w-auto mx-auto"
            />
          </Link>
          <h1 className="text-lg font-bold text-gray-900">Connexion</h1>
          <p className="text-gray-500 text-xs">Accède à tous tes univers</p>
        </div>

        {/* Carte de connexion - Fond orange doux */}
        <div className="rounded-2xl shadow-xl p-5 border border-orange-200" style={{ backgroundColor: '#FFF7ED' }}>
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Erreur */}
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-red-50 border border-red-200 rounded-xl p-2.5 flex items-center gap-2 text-red-700 text-sm"
              >
                <AlertCircle size={16} />
                {error}
              </motion.div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse email
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="rounded-xl bg-white"
                  style={{ paddingLeft: '44px' }}
                  required
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-xl bg-white"
                  style={{ paddingLeft: '44px', paddingRight: '40px' }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Mot de passe oublié */}
            <div className="text-right">
              <a href="#" className="text-xs text-orange-600 hover:text-orange-700">
                Mot de passe oublié ?
              </a>
            </div>

            {/* Bouton connexion */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl h-11 text-base font-semibold"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn size={18} className="mr-2" />
                  Se connecter
                </>
              )}
            </Button>
          </form>

          {/* Séparateur */}
          <div className="my-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">ou</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Inscription */}
          <div className="text-center">
            <p className="text-gray-600 text-xs">
              Pas encore de compte ?{' '}
              <Link to="/reseau/inscription" className="text-orange-600 hover:text-orange-700 font-medium">
                Créer un compte
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
